import { useState } from "react";
import { Infinity as InfinityIcon, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MODES } from "@/context/AppModeContext";

const STAGES = [
  { key: "child", label: "Child (5–10)" },
  { key: "teen", label: "Teenager (11–17)" },
  { key: "student", label: "College Student" },
  { key: "adult", label: "Adult" },
  { key: "professional", label: "Professional / Entrepreneur" },
  { key: "senior", label: "Senior" },
];

export default function Auth() {
  const { login, register, formatError } = useAuth();
  const [tab, setTab] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", life_stage: "adult" });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (tab === "login") await login(form.email, form.password);
      else await register(form);
      toast.success("Welcome to inizio");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-mode="adult">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 bg-card border-r border-border overflow-hidden">
        <div className="absolute inset-0 opacity-40" style={{ backgroundImage: "radial-gradient(hsl(var(--primary)/0.25) 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
        <div className="relative flex items-center gap-3">
          <div className="h-11 w-11 rounded-2xl bg-primary grid place-items-center text-primary-foreground"><InfinityIcon className="w-6 h-6" /></div>
          <span className="font-head font-extrabold text-xl">inizio</span>
        </div>
        <div className="relative space-y-5 max-w-md">
          <h1 className="font-head font-extrabold text-4xl xl:text-5xl leading-[1.05]">Learn life.<br />Practice life.<br /><span className="text-primary">Navigate life.</span></h1>
          <p className="text-muted-foreground text-lg">One adaptive intelligence for every stage of life — from a curious child to a busy professional to a confident senior.</p>
          <div className="flex flex-wrap gap-2 pt-2">
            {MODES.map((m) => <span key={m.key} className="rounded-full border border-border px-3 py-1 text-xs text-muted-foreground">{m.label}</span>)}
          </div>
        </div>
        <p className="relative text-xs text-muted-foreground">From childhood to old age — learn the skills life actually requires.</p>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-10 bg-background">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="auth-form">
          <div className="lg:hidden flex items-center gap-2 justify-center mb-2">
            <div className="h-9 w-9 rounded-xl bg-primary grid place-items-center text-primary-foreground"><InfinityIcon className="w-5 h-5" /></div>
            <span className="font-head font-extrabold text-lg">inizio</span>
          </div>

          <div className="flex rounded-full bg-secondary p-1">
            {["login", "register"].map((t) => (
              <button key={t} type="button" onClick={() => setTab(t)} data-testid={`tab-${t}`}
                className={`flex-1 rounded-full py-2 text-sm font-bold capitalize transition-colors ${tab === t ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
                {t === "login" ? "Log in" : "Sign up"}
              </button>
            ))}
          </div>

          {tab === "register" && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Your name</label>
              <input data-testid="name-input" value={form.name} onChange={set("name")} required className="w-full rounded-md border border-input bg-card px-3 py-2.5 outline-none focus:ring-2 focus:ring-ring" placeholder="Alex" />
            </div>
          )}

          <div className="space-y-1.5">
            <label className="text-sm font-medium">Email</label>
            <input data-testid="email-input" type="email" value={form.email} onChange={set("email")} required className="w-full rounded-md border border-input bg-card px-3 py-2.5 outline-none focus:ring-2 focus:ring-ring" placeholder="you@email.com" />
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">Password</label>
            <div className="relative">
              <input data-testid="password-input" type={show ? "text" : "password"} value={form.password} onChange={set("password")} required minLength={6} className="w-full rounded-md border border-input bg-card px-3 py-2.5 pr-10 outline-none focus:ring-2 focus:ring-ring" placeholder="••••••••" />
              <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground">{show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
            </div>
          </div>

          {tab === "register" && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Who is this for?</label>
              <Select value={form.life_stage} onValueChange={(v) => setForm((f) => ({ ...f, life_stage: v }))}>
                <SelectTrigger data-testid="stage-select"><SelectValue /></SelectTrigger>
                <SelectContent>{STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}

          <button type="submit" disabled={busy} data-testid="auth-submit" className="w-full rounded-full bg-primary text-primary-foreground font-bold py-3 hover:brightness-110 active:scale-[0.99] transition disabled:opacity-50">
            {busy ? "Please wait…" : tab === "login" ? "Log in" : "Create account"}
          </button>
        </form>
      </div>
    </div>
  );
}
